import {
    ActivityFilterProps,
    ActivityProps,
    ActivityTimeline,
    EmptyBox,
    ListActivityBone,
    Radio,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { DropdownButton } from '@europrocurement/flexy-components/components/molecules/DropdownButton';
import {
    AdaptiveActionBar,
    AdaptiveActionBarProps,
} from '@europrocurement/flexy-components/components/organisms/ActivityHistoryCard/fragments/AdaptativeActionBar';
import {
    Box,
    CardContent,
    CardHeader,
    FormControlLabel,
    MenuItem,
    Typography,
    useTheme,
} from '@mui/material';
import React from 'react';

type ActivitiesCardContentProps = {
    /** Liste des activités à afficher */
    activities: ActivityProps[];
    /** Callback pour éditer une note */
    onEditNote?: (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => void;
    /** Callback pour supprimer une note */
    onDeleteNote?: (noteId: string) => void;
    /** Titre du bloc */
    title?: string;
    /** Chargement des activités */
    activitiesLoading?: boolean;
    /** Chargement des notes */
    notesLoading?: boolean;
    /** Affichage d'erreur dans la card */
    notesError?: string | null;
};
const ActivitiesCardContent: React.FunctionComponent<ActivitiesCardContentProps> = function ({
    activities,
    onEditNote,
    onDeleteNote,
    title = 'Activités récentes',
    activitiesLoading = false,
    notesLoading = false,
    notesError = null,
}) {
    const { palette } = useTheme();
    const grey700 = palette.grey[700];

    const [activeNoteFilter, setActiveNoteFilter] = React.useState<
        'all_notes' | 'important_notes_only' | 'none'
    >('all_notes');
    const [activeActionFilter, setActiveActionFilter] = React.useState<string>('all_actions');

    const hasNotes = React.useMemo(() => activities.some((a) => a.type === 'note'), [activities]);
    const hasActions = React.useMemo(
        () => activities.some((a) => a.type === 'action'),
        [activities],
    );

    const actionOptions = React.useMemo(() => {
        const map = new Map<string, string>();
        activities.forEach((activity) => {
            if (activity.type === 'action' && activity.actionType) {
                if (!map.has(activity.actionType)) {
                    map.set(activity.actionType, activity.actionType);
                }
            }
        });

        return Array.from(map.entries())
            .map(([actionType]) => ({ value: actionType, label: actionType }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [activities]);

    const filterNoteOptions = React.useMemo(
        () =>
            [
                { value: 'all_notes', label: 'Toutes les notes' },
                { value: 'important_notes_only', label: 'Uniquement les notes importantes' },
                { value: 'none', label: 'Masquer les notes' },
            ] as const,
        [],
    );

    const filterLogsActionOptions = React.useMemo(
        () => [
            { value: 'all_actions', label: 'Toutes les actions' },
            ...actionOptions,
            { value: 'none_actions', label: 'Masquer les actions' },
        ],
        [actionOptions],
    );
    const filteredActivities = React.useMemo(
        () =>
            activities.filter((activity) => {
                // Filtrer les notes
                if (activity.type === 'note') {
                    if (activeNoteFilter === 'none') {
                        return false;
                    }
                    if (activeNoteFilter === 'all_notes') {
                        return true;
                    }
                    if (
                        activeNoteFilter === 'important_notes_only' &&
                        activity.noteType === 'important'
                    ) {
                        return true;
                    }
                    return false;
                }

                // Filtrer les actions
                if (activity.type === 'action') {
                    if (activeActionFilter === 'none_actions') {
                        return false;
                    }
                    if (activeActionFilter === 'all_actions') {
                        return true;
                    }
                    return activity.actionType === activeActionFilter;
                }

                return false;
            }),
        [activities, activeNoteFilter, activeActionFilter],
    );

    const countOfTotalActivities = activities.length;
    const countOfFilteredActivities = filteredActivities.length;

    const prevFiltersRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        const filtersObj = {
            note: hasNotes ? activeNoteFilter : null,
            action: hasActions ? activeActionFilter : null,
        };

        const serialized = JSON.stringify(filtersObj);

        if (serialized !== prevFiltersRef.current) {
            prevFiltersRef.current = serialized;

            const filters: ActivityFilterProps[] | string[] = [];
            if (hasNotes) filters.push(activeNoteFilter);
            if (hasActions) filters.push(activeActionFilter as string[]);
        }
    }, [activeNoteFilter, activeActionFilter, hasNotes, hasActions]);
    // --- Memo children for dropdowns ---
    const noteChildren = React.useMemo(
        () =>
            filterNoteOptions.map((option) => (
                <MenuItem key={option.value}>
                    <FormControlLabel
                        control={
                            <Radio
                                checked={activeNoteFilter === option.value}
                                onChange={() => setActiveNoteFilter(option.value)}
                            />
                        }
                        label={option.label}
                    />
                </MenuItem>
            )),
        [activeNoteFilter, filterNoteOptions],
    );

    const logChildren = React.useMemo(
        () =>
            filterLogsActionOptions.map((option) => (
                <MenuItem key={option.value}>
                    <FormControlLabel
                        control={
                            <Radio
                                checked={activeActionFilter === option.value}
                                onChange={() => setActiveActionFilter(option.value)}
                            />
                        }
                        label={option.label}
                    />
                </MenuItem>
            )),
        [activeActionFilter, filterLogsActionOptions],
    );

    const dropdownNoteAction: AdaptiveActionBarProps['dropdownAction'] = hasNotes
        ? {
              label:
                  filterNoteOptions.find((opt) => opt.value === activeNoteFilter)?.label ??
                  'Toutes les notes',
              color: grey700,
              variant: 'outlined',
              size: 'small',
              sx: { width: '100%' },
              children: noteChildren,
          }
        : undefined;

    const dropdownLogAction = hasActions
        ? {
              label:
                  filterLogsActionOptions.find((opt) => opt.value === activeActionFilter)?.label ??
                  'Toutes les actions',
              color: grey700,
              variant: 'outlined',
              size: 'small',
              sx: { width: '100%' },
              children: logChildren,
          }
        : undefined;

    return (
        <Box sx={{ padding: '0 20px 20px' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    paddingTop: '16px',
                    paddingBottom: '16px',
                }}
            >
                <CardHeader
                    title={title}
                    subtitle={
                        <Typography
                            variant="l2dsubtitles"
                            sx={{ marginLeft: '10px' }}
                        >
                            {`${countOfFilteredActivities}/${countOfTotalActivities}`}
                        </Typography>
                    }
                    headerSx={{ paddingTop: '0px' }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        flexGrow={2}
                        sx={{ minWidth: '100%' }}
                    >
                        <AdaptiveActionBar
                            actions={activities}
                            dropdownAction={dropdownNoteAction}
                        />
                    </Box>

                    <Box flexGrow={1}>
                        {dropdownLogAction && (
                            <DropdownButton
                                label={dropdownLogAction.label}
                                variant={dropdownLogAction.variant}
                                size={dropdownLogAction.size}
                                color={dropdownLogAction.color}
                            >
                                {dropdownLogAction.children}
                            </DropdownButton>
                        )}
                    </Box>
                </Box>
            </Box>
            <CardContent sx={{ padding: 0 }}>
                <SkeletonLoader
                    isLoading={activitiesLoading || notesLoading}
                    skeleton={
                        <Box
                            sx={{ display: 'flex', justifyContent: 'center', padding: '20px 20px' }}
                        >
                            <ListActivityBone itemCount={3} />
                        </Box>
                    }
                    isInError={!!notesError}
                    errorChildren={
                        <Box
                            sx={{
                                padding: '20px',
                                textAlign: 'center',
                                color: 'error.main',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <p>Erreur lors du chargement des notes</p>
                            <p style={{ fontSize: '0.9em', marginTop: '8px' }}>{notesError}</p>
                        </Box>
                    }
                >
                    <Box sx={{ padding: '0 20px 20px' }}>
                        {countOfFilteredActivities > 0 ? (
                            <ActivityTimeline
                                activities={filteredActivities}
                                onEditNote={onEditNote}
                                onDeleteNote={onDeleteNote}
                            />
                        ) : (
                            <EmptyBox
                                text="Pas d'activités à afficher"
                                sx={{
                                    marginBottom: '-40px',
                                }}
                            />
                        )}
                    </Box>
                </SkeletonLoader>
            </CardContent>
        </Box>
    );
};

export default ActivitiesCardContent;
